import { create } from 'zustand';
import { apiClient } from '@/services/api';
import { on as onEvent } from '@/services/events';

interface SiteConfig {
  siteName: string;
  siteDescription: string;
  siteLogo: string;
  siteKeywords: string;
  siteFooter: string;
  contactEmail: string;
  defaultCredits: number;
}

interface SiteConfigStore {
  config: SiteConfig | null;
  loading: boolean;
  load: () => Promise<void>;
  update: (config: SiteConfig) => void;
}

// 默认配置
const defaultConfig: SiteConfig = {
  siteName: 'Sora2 AI Platform',
  siteDescription: '让创意触手可及，让想象成为现实',
  siteLogo: '',
  siteKeywords: 'AI,视频生成,图像生成,Sora2',
  siteFooter: '© 2024 Sora2 AI Platform. All rights reserved.',
  contactEmail: 'contact@sora2.ai',
  defaultCredits: 100
};

export const useSiteConfigStore = create<SiteConfigStore>((set, get) => {
  // 监听配置更新事件
  onEvent('config_updated', (data: any) => {
    if (data.type === 'site_config' || data.type === 'system_config') {
      // 提取站点配置相关字段
      const siteConfig: SiteConfig = {
        siteName: data.data?.siteName || get().config?.siteName || defaultConfig.siteName,
        siteDescription: data.data?.siteDescription || get().config?.siteDescription || defaultConfig.siteDescription,
        siteLogo: data.data?.siteLogo || get().config?.siteLogo || defaultConfig.siteLogo,
        siteKeywords: data.data?.siteKeywords || get().config?.siteKeywords || defaultConfig.siteKeywords,
        siteFooter: data.data?.siteFooter || get().config?.siteFooter || defaultConfig.siteFooter,
        contactEmail: data.data?.contactEmail || get().config?.contactEmail || defaultConfig.contactEmail,
        defaultCredits: data.data?.defaultCredits || get().config?.defaultCredits || defaultConfig.defaultCredits
      };
      set({ config: siteConfig });
    }
  });

  return {
    config: defaultConfig, // 初始使用默认配置
    loading: false,

    load: async () => {
      set({ loading: true });
      try {
        // 尝试从公共API获取配置（不需要登录）
        const response = await apiClient.get('/public/site-config');
        if (response.success && response.data) {
          // 提取站点配置相关字段
          const siteConfig: SiteConfig = {
            siteName: response.data.siteName || defaultConfig.siteName,
            siteDescription: response.data.siteDescription || defaultConfig.siteDescription,
            siteLogo: response.data.siteLogo || defaultConfig.siteLogo,
            siteKeywords: response.data.siteKeywords || defaultConfig.siteKeywords,
            siteFooter: response.data.siteFooter || defaultConfig.siteFooter,
            contactEmail: response.data.contactEmail || defaultConfig.contactEmail,
            defaultCredits: response.data.defaultCredits || defaultConfig.defaultCredits
          };
          set({ config: siteConfig, loading: false });
        } else {
          set({ config: defaultConfig, loading: false });
        }
      } catch (error) {
        // 加载失败，使用默认配置（不显示错误）
        console.log('使用默认站点配置');
        set({ config: defaultConfig, loading: false });
      }
    },

    update: (config: SiteConfig) => {
      set({ config });
    }
  };
});

